import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import Navbar from "../sections/Navbar";
import Footer from "../sections/Footer";
import SEO from "../SEO";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import AuthModal from "./AuthModal";
import usePlanPurchase from "../../hooks/usePlanPurchase";
import { getPlan } from "../../lib/masterApi";
import { captureSessionFromSearch } from "../../lib/planSession";
import { formatPrice, intervalLabel, planFeatures } from "../../lib/plans";

export default function PlanDetailPage() {
  const { id } = useParams();
  const { search, pathname } = useLocation();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(false);
  const purchase = usePlanPurchase();

  // Store token/returnUrl from the panel link, then scrub them from the URL.
  useEffect(() => {
    if (captureSessionFromSearch(search)) window.history.replaceState(null, "", pathname);
  }, [search, pathname]);

  useEffect(() => {
    let live = true;
    setPlan(null);
    setError(false);
    getPlan(id)
      .then((p) => live && setPlan(p))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [id]);

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="plan-detail-page">
      <SEO title={`${plan?.name || "Plan"} | MagicBolt AI`} description="Activate your MagicBolt AI plan." path={`/plans/${id}`} noindex />
      <Navbar />
      <main className="mx-auto max-w-2xl px-4 pb-24 pt-32 sm:px-6">
        <Link to="/plans" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#FF6B00]" data-testid="back-to-plans">
          <ArrowLeft className="h-4 w-4" /> Back to All Plans
        </Link>

        {!plan && !error && <Skeleton className="mt-6 h-96 rounded-xl" />}
        {error && (
          <div className="mt-6 rounded-xl border border-[#E6E9F1] bg-white p-8 text-center" data-testid="plan-error">
            <p className="text-slate-700">This plan isn't available.</p>
            <Button asChild className="mt-4 bg-[#FF6B00] text-white hover:bg-[#e55f00]">
              <Link to="/plans">Browse all plans</Link>
            </Button>
          </div>
        )}
        {plan && (
          <div className="mt-6 rounded-xl border border-[#E6E9F1] bg-white p-8 mb-card-shadow">
            {plan.groupName && <p className="text-xs font-bold uppercase tracking-wide text-[#FF6B00]">{plan.groupName}</p>}
            <h1 className="mt-1 text-3xl font-extrabold text-slate-900">{plan.name}</h1>
            {plan.description && <p className="mt-2 text-slate-600">{plan.description}</p>}
            <div className="mt-6 flex items-baseline gap-1">
              {plan.isFree ? (
                <span className="text-4xl font-extrabold">Free</span>
              ) : (
                <>
                  <span className="text-4xl font-extrabold">{formatPrice(plan.price)}</span>
                  <span className="text-slate-500">/ {intervalLabel(plan.billingInterval)}</span>
                </>
              )}
            </div>
            <ul className="mt-6 space-y-2.5">
              {planFeatures(plan.limits).map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-[#FF6B00]" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => purchase.purchase(plan)}
              disabled={Boolean(purchase.busyPlanId)}
              className="mt-8 w-full bg-[#FF6B00] py-6 text-base text-white hover:bg-[#e55f00]"
              data-testid="master-plan-purchase"
            >
              {purchase.busyPlanId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Choose Plan
            </Button>
          </div>
        )}
      </main>
      <Footer />
      <AuthModal
        open={Boolean(purchase.authPlan)}
        planName={purchase.authPlan?.name}
        onClose={purchase.closeAuth}
        onAuthenticated={purchase.onAuthenticated}
      />
    </div>
  );
}
